// AlreadyQuoteGiven.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Globe } from "lucide-react";

const AlreadyQuoteGiven = ({ email_id, website_id, setAlreadyGiven }) => {
  const [showModal, setShowModal] = useState(false);
  const [quoteDetails, setQuoteDetails] = useState([]);
  const [services, setServices] = useState([]);
  const fetchServices = async () => {
    try {
      const response = await fetch(
        "https://loopback-skci.onrender.com/api/scope/getAllServices",
        {
          method: "POST", // Use POST method
          headers: {
            "Content-Type": "application/json", // Set content type to JSON
          },
          body: JSON.stringify(), // Pass the POST data as JSON
        },
      );

      const data = await response.json(); // Parse the response as JSON
      if (data.status) {
        setServices(data.data);
      } else {
        console.error("Failed to fetch Services:", data.message);
      }
    } catch (error) {
      console.error("Error fetching Services:", error);
    }
  };

  useEffect(() => {
    const fetchQuoteData = async () => {
      try {
        // Step 1: Initial fast quote list fetch
        const response = await axios.post(
          "https://loopback-skci.onrender.com/api/scope/checkpresentemail",
          { email_id, website_id },
        );

        const quoteData = response.data?.quote_details || [];

        // Step 2: Set the basic data immediately (without website_name)
        setQuoteDetails(quoteData);

        // Step 3: For each quote, fetch its website_name separately
        quoteData.forEach(async (quote, index) => {
          try {
            const detailRes = await fetch(
              "https://loopback-skci.onrender.com/api/scope/view_query_details_api",
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ query_id: quote.ref_id }),
              },
            );

            const detailData = await detailRes.json();

            if (detailData.status && detailData?.queryInfo) {
              const website_name = detailData.queryInfo.website_name || null;

              // Step 4: Update this specific quote with website_name
              setQuoteDetails((prev) =>
                prev.map((q, i) =>
                  i === index
                    ? {
                      ...q,
                      website_name,
                      ...detailData.data,
                    }
                    : q,
                ),
              );
            } else {
              console.warn(
                `Detail fetch failed for ref_id ${quote.ref_id}: ${detailData.message}`,
              );
            }
          } catch (err) {
            console.error(
              `Error fetching details for ref_id ${quote.ref_id}`,
              err,
            );
          }
        });
      } catch (err) {
        console.error("Error fetching quote data:", err);
      }
    };

    if (email_id && website_id) {
      fetchQuoteData();
    }

    fetchServices();
  }, [email_id, website_id]);

  useEffect(() => {
    setAlreadyGiven(quoteDetails?.length > 0);
  }, [quoteDetails]);

  if (!quoteDetails.length) return null;

  return (
    <div>
      <button
        className={`text-orange-600 bg-yellow-100 flex items-center hover:underline border-yellow-500 border-1 w-full text-sm px-1 py-0.5`}
        style={{
          fontSize: "11px",
        }}
        onClick={() => setShowModal(true)}
      >
        <Globe size={16} className="mr-1" /> Quote For Other Website
      </button>

      <AnimatePresence>
        {showModal && (
          <motion.div
            className="modal-backdrop h-screen fixed inset-0 z-50 bg-black/30 bg-opacity-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowModal(false)}
          >
            <motion.div
              className="bg-white px-6 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-3 border-b py-3 sticky top-0 bg-white">
                <h2 className="  text-gray-800 text-lg font-semibold  ">
                  Quote Details
                </h2>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => setShowModal(false)}
                >
                  Close
                </button>
              </div>

              {quoteDetails.map((quote, index) => {
                const plans = quote.plan ? quote.plan.split(",") : (quote.selected_plans ? quote.selected_plans.split(",") : []);
                const comments = quote.plan_comments ? JSON.parse(quote.plan_comments || "{}") : {};
                const wordCounts = quote.word_counts ? JSON.parse(quote.word_counts || "{}") : {};

                const standardInfo = quote.standard_info ? JSON.parse(quote.standard_info) : null;
                const basicInfo = quote.basic_info ? JSON.parse(quote.basic_info) : null;
                const advancedInfo = quote.advanced_info ? JSON.parse(quote.advanced_info) : null;

                const getPlanInfo = (planName) => {
                  if (planName === "Standard") return standardInfo;
                  if (planName === "Basic") return basicInfo;
                  if (planName === "Advanced") return advancedInfo;
                  return null;
                };

                const serviceIds = quote?.service_name
                  ? String(quote.service_name).split(",").map((id) => id.trim())
                  : [];

                const serviceNames = serviceIds.map((id) => {
                  const service = services.find((s) => String(s.id) === id);
                  return service ? service.name : `Service #${id}`;
                });
                return (
                  <div
                    key={index}
                    className="mb-8 text-gray-800 pr-3 space-y-4 py-6 border-b last:border-0"
                  >
                    <div className="flex items-center flex-wrap gap-2">
                      {quote.website_name && (
                        <p className="flex items-center text-blue-500 bg-gray-100 px-2 rounded border border-gray-200 py-1 text-xs">
                          <Globe size={14} className="text-blue-600 mr-1" />
                          Website: <span className="text-black font-medium ml-1">{quote.website_name}</span>
                        </p>
                      )}
                      {quote.service_name && (
                        <p className="flex items-center text-blue-500 bg-gray-100 px-2 rounded border border-gray-200 py-1 text-xs">
                          <FileText size={14} className="text-blue-600 mr-1" />
                          Service: <span className="text-black font-medium ml-1">{serviceNames.join(", ")}</span>
                        </p>
                      )}
                    </div>

                    {quote.quote_heading && (
                      <h3 className="text-md font-bold text-gray-900 mt-2">
                        {quote.quote_heading}
                      </h3>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-2">
                      <div className="p-2 border rounded bg-gray-50">
                        <p className="text-[10px] text-gray-500 uppercase font-semibold">Quote ID</p>
                        <p className="text-sm">{quote.id}</p>
                      </div>
                      <div className="p-2 border rounded bg-gray-50">
                        <p className="text-[10px] text-gray-500 uppercase font-semibold">Ref ID</p>
                        <p className="text-sm">{quote.ref_id}</p>
                      </div>
                      <div className="p-2 border rounded bg-gray-50">
                        <p className="text-[10px] text-gray-500 uppercase font-semibold">Client Name</p>
                        <p className="text-sm">{quote.client_name}</p>
                      </div>
                      <div className="p-2 border rounded bg-gray-50">
                        <p className="text-[10px] text-gray-500 uppercase font-semibold">Subject Area</p>
                        <p className="text-sm">{quote.subject_area || "N/A"}</p>
                      </div>
                    </div>

                    {quote.feasability_comments && (
                      <div className="mb-4">
                        <h4 className="text-sm font-semibold mb-1 text-gray-700">Scope of Work / Comments</h4>
                        <div
                          className="text-xs p-3 border rounded bg-blue-50/30 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: quote.feasability_comments }}
                        />
                      </div>
                    )}

                    <div className="space-y-6">
                      {plans.map((planName, i) => {
                        const info = getPlanInfo(planName);

                        if (info) {
                          return (
                            <div key={i} className="border rounded-lg overflow-hidden border-orange-200 bg-white">
                              <div className="bg-orange-50 px-4 py-2 border-b border-orange-200 flex justify-between items-center">
                                <h4 className="font-bold text-orange-800">{planName} Plan</h4>
                                <div className="text-sm font-semibold">
                                  {info.currency} {info.price} {info.noOfWords && `(${info.noOfWords} words)`}
                                </div>
                              </div>
                              <div className="p-0 overflow-x-auto">
                                <table className="w-full text-xs text-left">
                                  <thead className="bg-gray-50 border-b">
                                    <tr>
                                      <th className="px-4 py-2 font-semibold text-gray-600">Milestone</th>
                                      <th className="px-4 py-2 font-semibold text-gray-600">Details</th>
                                      <th className="px-4 py-2 font-semibold text-gray-600">Timeline</th>
                                      <th className="px-4 py-2 font-semibold text-gray-600 text-right">Amount</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y">
                                    {info.milestoneData?.map((ms, j) => (
                                      <React.Fragment key={j}>
                                        <tr className="bg-white hover:bg-gray-50">
                                          <td className="px-4 py-3 font-medium align-top" rowSpan={ms.parametersData?.length || 1}>
                                            <div className="whitespace-pre-line">{ms.name}</div>
                                            <div className="text-[10px] text-gray-500 mt-1">({ms.percentage}%)</div>
                                          </td>
                                          {ms.parametersData?.length > 0 ? (
                                            <>
                                              <td className="px-4 py-3 align-top">{ms.parametersData[0].parameters}</td>
                                              <td className="px-4 py-3 align-top whitespace-nowrap">{ms.parametersData[0].time_frame}</td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="px-4 py-3">-</td>
                                              <td className="px-4 py-3">-</td>
                                            </>
                                          )}
                                          <td className="px-4 py-3 text-right align-top font-semibold" rowSpan={ms.parametersData?.length || 1}>
                                            {info.currency} {ms.price}
                                          </td>
                                        </tr>
                                        {ms.parametersData?.slice(1).map((param, k) => (
                                          <tr key={k} className="bg-white hover:bg-gray-50">
                                            <td className="px-4 py-3 align-top border-l">{param.parameters}</td>
                                            <td className="px-4 py-3 align-top whitespace-nowrap border-l">{param.time_frame}</td>
                                          </tr>
                                        ))}
                                      </React.Fragment>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              {info.tandc && (
                                <div className="p-3 bg-gray-50 border-t text-[10px] text-gray-600">
                                  <p className="font-semibold mb-1">Terms & Conditions:</p>
                                  <div dangerouslySetInnerHTML={{ __html: info.tandc }} />
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Fallback to old rendering if no info object
                        return (
                          <div key={i} className="mb-4">
                            <h4 className="text-md font-semibold mb-2">{planName} Plan</h4>
                            <div className="overflow-auto border rounded">
                              <table className="w-full text-xs text-left">
                                <thead className="bg-gray-100">
                                  <tr>
                                    <th className="border px-3 py-2">Comments</th>
                                    <th className="border px-3 py-2">Word Count</th>
                                    <th className="border px-3 py-2 text-right">Final Price</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr className="hover:bg-gray-50">
                                    <td
                                      className="border px-3 py-2"
                                      dangerouslySetInnerHTML={{
                                        __html: comments[planName] || "-",
                                      }}
                                    ></td>
                                    <td className="border px-3 py-2">
                                      {wordCounts[planName] || "-"}
                                    </td>
                                    <td className="border px-3 py-2 text-right font-semibold">
                                      {quote.tasks?.map((task, index) => {
                                        const finals = task.final_price?.split(",") || [];
                                        return <div key={index}>{finals[i] ?? "-"}</div>;
                                      })}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AlreadyQuoteGiven;
